import { useEffect, type FC, type MouseEventHandler } from 'react';
import { createFragmentContainer, graphql, type RelayProp } from 'react-relay/legacy.js';
import { FormattedMessage } from 'dibs-react-intl';

// components
import { Link } from 'dibs-elements/exports/Link.tsx';

import { PdpSharedShippingAndReturnsModalLoader } from '../PdpSharedModals/PdpSharedShippingModal/PdpSharedShippingAndReturnsModalLoader.tsx';
import { useCurrency } from 'dibs-buyer-layout/exports/useCurrency.tsx';
import { useShippingAndReturns } from '../hooks/useShippingAndReturns.ts';
import { usePdpShippingInfoQuery } from '../PdpSharedShippingInfo/hooks/usePdpShippingInfoQuery.ts';

// helpers
import * as tracking from 'dibs-tracking';

// styles
import styles from '../shared/PdpSharedDetail/PdpSharedDetail.scss';

import { type PdpSharedDetailReturnPolicy_item$data } from './__generated__/PdpSharedDetailReturnPolicy_item.graphql.ts';
import { type PdpSharedDetailReturnPolicy_user$data } from './__generated__/PdpSharedDetailReturnPolicy_user.graphql.ts';
import { type PdpSharedDetailReturnPolicy_sku$data } from './__generated__/PdpSharedDetailReturnPolicy_sku.graphql.ts';

type Props = {
    item: PdpSharedDetailReturnPolicy_item$data;
    user?: PdpSharedDetailReturnPolicy_user$data | null;
    sku?: PdpSharedDetailReturnPolicy_sku$data | null;
    itemId: string;
    userId?: string | null;
    itemQuantity?: number | null;
    isClient?: boolean;
    relay: RelayProp;
};

const PdpSharedDetailReturnPolicyComponent: FC<Props> = ({
    item,
    user,
    sku,
    userId,
    itemId,
    relay,
    itemQuantity,
    isClient,
}) => {
    const { currency } = useCurrency();

    const skuId = sku?.serviceId || item?.defaultSkuId || '';
    const { reloadQuery } = usePdpShippingInfoQuery({
        itemId,
        userId,
        isTradeUser: user?.isVerifiedTrade,
        isClient,
        itemQuantity,
        skuId,
    });

    const {
        modalOpen,
        openShippingModal,
        showAuthModal,
        showCompleteAccount,
        closeShippingModal,
        onSuccessfulPreQuoteRequest,
    } = useShippingAndReturns({
        user,
        isSidebar: false,
        initialTab: 'returns',
        onSuccessfulPreQuoteRequest: args => reloadQuery?.(args),
    });

    useEffect(() => {
        const urlParams = new URLSearchParams(location.search);
        if (urlParams.get('modal') === 'returnPolicy') {
            openShippingModal();
        }
    }, [openShippingModal]);

    const onClick: MouseEventHandler<HTMLAnchorElement> = e => {
        e.preventDefault();
        openShippingModal();

        // the following was originally in callback to this.setState
        const returnable = item?.returnPolicy?.accept;

        tracking.trackEvent({
            category: 'product',
            action: 'view return details',
            label: `pdp description|${returnable ? 'returns accepted' : 'returns not accepted'}`,
        });
    };

    const buyerCopy = item?.returnPolicy?.buyerCopy;
    const returnPolicySection = buyerCopy?.returnPolicySection?.htmlCopy || '';
    const customizableCopy = buyerCopy?.customizableDetailReturnCopy?.htmlCopy || '';
    const showCustomizableCopy = !!customizableCopy;
    return (
        <span>
            {/* Item Return Policy */}
            <span
                className={styles.returnPolicy}
                dangerouslySetInnerHTML={{ __html: returnPolicySection }}
            />
            {showCustomizableCopy && (
                <>
                    {' '}
                    <span
                        className={styles.returnPolicy}
                        dangerouslySetInnerHTML={{ __html: customizableCopy }}
                    />
                </>
            )}
            {/* See Details */}{' '}
            <Link onClick={onClick} className={styles.printHide}>
                <FormattedMessage
                    id="abf.PdpSharedDetails.PdpSharedDetailReturnPolicy.details"
                    defaultMessage="Details"
                />
            </Link>
            {/* Shipping and Return Policy Modal */}
            {modalOpen && (
                <PdpSharedShippingAndReturnsModalLoader
                    currency={currency}
                    userId={userId}
                    itemId={itemId}
                    environment={relay.environment}
                    hasQuote={item?.hasShipmentQuotes || null}
                    isOpened={modalOpen}
                    closeShippingModal={closeShippingModal}
                    trackingLocation="pdp shipping details modal"
                    showAuthModal={showAuthModal}
                    showCompleteAccount={showCompleteAccount}
                    openModal={openShippingModal}
                    onSuccessfulPreQuoteRequest={onSuccessfulPreQuoteRequest}
                    itemQuantity={itemQuantity}
                    skuId={skuId}
                    activeTab="returns"
                />
            )}
        </span>
    );
};

export const PdpSharedDetailReturnPolicy = createFragmentContainer(
    PdpSharedDetailReturnPolicyComponent,
    {
        sku: graphql`
            fragment PdpSharedDetailReturnPolicy_sku on ItemSkuType {
                serviceId
            }
        `,
        user: graphql`
            fragment PdpSharedDetailReturnPolicy_user on User {
                isVerifiedTrade
                ...useShippingAndReturns_user
            }
        `,
        item: graphql`
            fragment PdpSharedDetailReturnPolicy_item on Item {
                hasShipmentQuotes
                defaultSkuId
                returnPolicy(userId: $userId) {
                    accept
                    buyerCopy {
                        returnPolicySection(userCountryCode: $userCountryCode)
                            @include(if: $afterDisplay) {
                            htmlCopy
                        }
                        customizableDetailReturnCopy {
                            htmlCopy
                        }
                    }
                }
            }
        `,
    }
);
